﻿import { FC } from 'react';
import { Container } from '../ui/Container';
import { siteConfig } from '../../config/site';
import { ROUTES } from '../../config/routes';
import { getPaidProducts, formatPriceINR } from '../../config/catalog';

export interface FooterProps {
  onNavigate: (path: string) => void;
}

export const Footer: FC<FooterProps> = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();
  const products = getPaidProducts();

  return (
    <footer
      style={{
        backgroundColor: 'var(--color-navy-dark)',
        color: '#cbd5e1',
        borderTop: '1px solid var(--color-navy-border)',
        marginTop: 'auto',
        padding: '4.5rem 0 2.5rem 0',
        fontSize: '0.9375rem'
      }}
    >
      <Container size="xl">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '2.5rem',
            marginBottom: '3.5rem'
          }}
        >
          {/* Brand & Mission Column */}
          <div style={{ maxWidth: '320px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div
                style={{
                  width: '2.25rem',
                  height: '2.25rem',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-brand-primary)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '1rem'
                }}
              >
                CS
              </div>
              <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#ffffff' }}>
                {siteConfig.siteName}
              </span>
            </div>
            <p style={{ color: '#94a3b8', lineHeight: 1.6, fontSize: '0.875rem' }}>
              Transforming careers through structured, personalized coaching and empowering growing companies with practical HR & business support.
            </p>
            <div style={{ marginTop: '1.25rem', fontSize: '0.8rem', color: '#64748b' }}>
              Founded by Nilesh Mishra • Certified HRM (CHRM)
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 style={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 700, marginBottom: '1.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Navigation
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                { label: 'Home', path: ROUTES.HOME },
                { label: 'Career Coaching', path: ROUTES.CAREER_COACHING },
                { label: 'Business Solutions', path: ROUTES.BUSINESS_SOLUTIONS },
                { label: 'Digital Toolkits', path: ROUTES.DIGITAL_PRODUCTS },
                { label: 'About Us', path: ROUTES.ABOUT },
                { label: 'Free Interview Guide', path: ROUTES.FREE_INTERVIEW_GUIDE },
                { label: 'Contact & Consultation', path: ROUTES.CONTACT }
              ].map((item, idx) => (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={() => onNavigate(item.path)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#cbd5e1',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontFamily: 'inherit',
                      textAlign: 'left'
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Digital Toolkits Column */}
          <div>
            <h4 style={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 700, marginBottom: '1.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Digital Toolkits
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {products.map(p => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => onNavigate(p.routePath)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#cbd5e1',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '0.5rem'
                    }}
                  >
                    <span>{p.title}</span>
                    <span style={{ color: '#38bdf8', fontSize: '0.8rem', fontWeight: 600 }}>
                      ({formatPriceINR(p.priceINR)})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Support Column */}
          <div>
            <h4 style={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 700, marginBottom: '1.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Legal & Support
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                { label: 'Privacy Policy', path: ROUTES.PRIVACY_POLICY },
                { label: 'Terms of Service', path: ROUTES.TERMS },
                { label: 'Refund Policy', path: ROUTES.REFUND_POLICY },
                { label: 'Digital Download Center', path: ROUTES.DOWNLOAD }
              ].map((item, idx) => (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={() => onNavigate(item.path)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#cbd5e1',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontFamily: 'inherit',
                      textAlign: 'left'
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>

            <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--color-navy-border)', fontSize: '0.85rem' }}>
              <div style={{ color: '#94a3b8' }}>Direct Inquiries:</div>
              <a href={`mailto:${siteConfig.contactEmail}`} style={{ color: '#38bdf8', fontWeight: 600 }}>
                {siteConfig.contactEmail}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Legal Copyright Bar */}
        <div
          style={{
            borderTop: '1px solid var(--color-navy-border)',
            paddingTop: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            fontSize: '0.8125rem',
            color: '#94a3b8'
          }}
        >
          <div>
            © {currentYear} {siteConfig.legal.companyName}. All rights reserved.
          </div>
          <div>
            Professional career coaching, talent acquisition & operational HR advisory.
          </div>
        </div>
      </Container>
    </footer>
  );
};


